import { createContext, useContext } from 'react';
import { useTheme } from '@emotion/react';
import type { Env0Theme } from 'constants/themes.constants';

/**
 * The z-index of the nearest enclosing Modal or Drawer wrapper; undefined at the top level.
 *
 * antd 5.29 stacks popups through its own zIndexContext, but seeds it from the global `zIndexPopupBase`
 * (the DS dropdown layer, 1000), not from the layer the container actually paints at. With the DS ladder
 * putting Modal at 1300 and Drawer at 1200, a modal opened from inside either got inline 1200 and rendered
 * behind its parent (the VCS connection "Add" flow). The wrappers hand antd an explicit z-index instead -
 * the DS layer at the top level, one step above the parent when nested - and antd seeds its own context
 * from that value, so the Selects, Popovers and Tooltips inside still stack above.
 */
export const PopupLayerContext = createContext<number | undefined>(undefined);

/** antd's `CONTAINER_OFFSET`: the step a nested container takes above its parent. */
export const POPUP_NESTING_STEP = 100;

export type PopupLayer = keyof Env0Theme['zIndex'];

/**
 * The z-index a Modal or Drawer wrapper hands antd. `override` is the call site's own `zIndex` and always
 * wins. Otherwise the DS `layer` at the top level, else the larger of that layer and one step above the
 * enclosing wrapper.
 */
export const usePopupLayerZIndex = (layer: PopupLayer, override?: number): number | undefined => {
  const theme = useTheme();
  const parent = useContext(PopupLayerContext);
  // A spec that renders without the app's ThemeProvider gets `{}` from emotion, so antd keeps its stacking.
  if (override !== undefined || !theme.zIndex) return override;

  const value = theme.zIndex[layer];
  return parent === undefined ? value : Math.max(parent + POPUP_NESTING_STEP, value);
};
